/**
 * 空白处建节点菜单：双击画布空白处（或右键空白处）弹出，选择创建新节点类型。
 *
 * 与 ConnectionCreateMenu 的区别：不与任何节点连线，在弹出的画布坐标处直接创建。
 * 使用 createPortal 渲染到 document.body。
 */
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Type, ImageIcon, Wand2, Video, Images, Film, SquareDashed, Bot, MessageSquare, Clapperboard, Palette, Music } from 'lucide-react';
import type { CanvasNodeType } from './types';
import { boardActions } from './state/boardStore';
import { makeNode } from './utils';
import { useMenuExternalDismiss } from './hooks/useMenuExternalDismiss';
import { readNodeParamMemory } from './utils/nodeParamMemory';
import { StyleCardPicker } from './nodes/StyleCardPicker';

interface NodeCreateMenuProps {
  /** 屏幕坐标（菜单定位用） */
  x: number;
  y: number;
  /** 画布坐标（节点落点，一般为光标位置） */
  canvasX: number;
  canvasY: number;
  onClose: () => void;
}

interface CreateMenuItem {
  type: CanvasNodeType;
  label: string;
  icon: React.ReactNode;
  color: string;
}

interface CreateMenuGroup {
  label: string;
  items: CreateMenuItem[];
}

export const NODE_CREATE_GROUPS: CreateMenuGroup[] = [
  {
    label: '文本',
    items: [
      { type: 'text', label: '文本节点', icon: <Type className="h-4 w-4" />, color: 'text-slate-600' },
      { type: 'llm', label: 'LLM 节点', icon: <MessageSquare className="h-4 w-4" />, color: 'text-amber-600' },
    ],
  },
  {
    label: '图片',
    items: [
      { type: 'image', label: '图片节点', icon: <ImageIcon className="h-4 w-4" />, color: 'text-emerald-600' },
      { type: 'generate_image', label: '生图配置', icon: <Wand2 className="h-4 w-4" />, color: 'text-blue-600' },
      { type: 'agent', label: '智能体', icon: <Bot className="h-4 w-4" />, color: 'text-indigo-600' },
      { type: 'image_group', label: '图片组', icon: <Images className="h-4 w-4" />, color: 'text-orange-600' },
    ],
  },
  {
    label: '视频',
    items: [
      { type: 'video', label: '视频节点', icon: <Video className="h-4 w-4" />, color: 'text-cyan-600' },
      { type: 'audio', label: '声音节点', icon: <Music className="h-4 w-4" />, color: 'text-pink-600' },
      { type: 'generate_video', label: '生视频配置', icon: <Clapperboard className="h-4 w-4" />, color: 'text-purple-600' },
      { type: 'video_group', label: '视频组', icon: <Film className="h-4 w-4" />, color: 'text-violet-600' },
    ],
  },
  {
    label: '风格',
    items: [
      { type: 'style_card', label: '风格卡片', icon: <Palette className="h-4 w-4" />, color: 'text-fuchsia-600' },
    ],
  },
  {
    label: '容器',
    items: [
      { type: 'group', label: '分组容器', icon: <SquareDashed className="h-4 w-4" />, color: 'text-slate-500' },
    ],
  },
];

/** 扁平化列表（供 ConnectionCreateMenu 等复用） */
export const NODE_CREATE_ITEMS: CreateMenuItem[] = NODE_CREATE_GROUPS.flatMap((g) => g.items);

/**
 * 各节点类型的默认 data（与 ConnectionCreateMenu 保持一致）。
 *
 * 生成配置类节点（生图 / 生视频 / 智能体）在硬编码默认值之上叠加「上次使用的参数记忆」，
 * 新建时自动套用用户上次选的模型 / 时长 / 清晰度 / 比例等（模型失效由节点组件回退兜底）。
 */
export function defaultNodeData(type: CanvasNodeType): Record<string, unknown> {
  switch (type) {
    case 'text':
      return { content: '' };
    case 'llm':
      return {};
    case 'image':
      return { imageUrl: null, status: null };
    case 'generate_image':
      return {
        mode: 'text2img',
        aspectRatio: 'auto',
        quality: 'low',
        count: 1,
        ...readNodeParamMemory('generate_image'),
      };
    case 'agent':
      return {
        status: 'idle',
        aspectRatio: 'auto',
        quality: 'low',
        ...readNodeParamMemory('agent'),
      };
    case 'generate_video':
      return {
        mode: 'text2video',
        duration: 5,
        resolution: '720p',
        aspectRatio: '16:9',
        // 配音默认开（与视频页一致）：模型 + 模式不支持音频时节点不显示该开关、
        // 提交前按能力收敛为关闭；用户显式关过则由「上次参数记忆」覆盖
        enableAudio: true,
        // 生成数量默认 1（>1 时结果收进视频组，与视频工作台的默认值一致）
        count: 1,
        ...readNodeParamMemory('generate_video'),
      };
    case 'image_group':
      return { jobIds: [], imageUrls: [] };
    case 'audio':
      return { audioStorageKey: null, fileName: null, durationSec: null };
    case 'video_group':
      return { videoUrls: [] };
    case 'style_card':
      // 风格卡片由选择面板创建，不会走 defaultNodeData 路径
      return { styleId: '', name: '', coverUrl: '', prompt: '' };
    default:
      return {};
  }
}

/** 把节点创建在以画布坐标为中心的位置（分组容器置于成员节点之下层） */
export function createNodeAtCenter(type: CanvasNodeType, canvasX: number, canvasY: number) {
  const node = makeNode(type, { x: 0, y: 0 }, defaultNodeData(type));
  // makeNode 会按类型回填默认尺寸，这里再以光标为中心放置
  node.x = canvasX - node.width / 2;
  node.y = canvasY - node.height / 2;
  if (type === 'group') node.zIndex = -1;
  boardActions.addNode(node);
  return node;
}

export function NodeCreateMenu({ x, y, canvasX, canvasY, onClose }: NodeCreateMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // 点击外部 / Escape / 画布缩放时关闭（见 useMenuExternalDismiss 注释）
  useMenuExternalDismiss(menuRef, onClose);

  const handleSelect = (type: CanvasNodeType) => {
    if (type === 'style_card') {
      setPickerOpen(true);
      return;
    }
    createNodeAtCenter(type, canvasX, canvasY);
    onClose();
  };

  if (pickerOpen) {
    return <StyleCardPicker canvasX={canvasX} canvasY={canvasY} onClose={onClose} />;
  }

  // 确保菜单不超出视口
  const menuWidth = 180;
  const menuHeight = 220;
  const posX = Math.min(x, window.innerWidth - menuWidth - 8);
  const posY = Math.min(y, window.innerHeight - menuHeight - 8);

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[10001] min-w-[180px] overflow-hidden rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl shadow-slate-950/10"
      style={{ left: posX, top: posY }}
    >
      {NODE_CREATE_GROUPS.map((group, gi) => (
        <div key={group.label}>
          {gi > 0 && <div className="my-1 h-px bg-slate-100" />}
          <div className="px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
            {group.label}
          </div>
          {group.items.map((item) => (
            <button
              key={item.type}
              type="button"
              onClick={() => handleSelect(item.type)}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
            >
              <span className={item.color}>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      ))}
    </div>,
    document.body
  );
}
